import React, { useState } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useQueryClient } from '@tanstack/react-query';

import {
  FeedGalleryPayload, GalleryImage, getGalleryGroup,
} from '../../services/gallery_api';
import GalleryLightbox from './GalleryLightbox';

interface Props {
  gallery: FeedGalleryPayload;
  isPublic?: boolean;
}

/**
 * アクティビティフィードのギャラリー投稿サムネ帯（最大4枚=2×2＋「+N」）。
 * サムネ/+N クリックで共通ライトボックスをグループ全画像で開く（リアクション込み）。
 * ギャラリータブへの導線は親ヘッダー側（「N枚アップロード」テキスト横）に置く。
 */
const GalleryActivityCard: React.FC<Props> = ({ gallery, isPublic }) => {
  const queryClient = useQueryClient();
  const [groupImages, setGroupImages] = useState<GalleryImage[]>([]);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);

  // オーナー（自分のギャラリー閲覧）以外はネタバレ画像をぼかす
  const isOwner = !isPublic;
  const thumbs = gallery.images.slice(0, 4);
  const extra = gallery.count - thumbs.length; // 「+N」表記に使う超過枚数（5枚以上のとき>0）

  const openAt = async (imageId: number) => {
    if (loading) return;
    setLoading(true);
    try {
      const imgs = await queryClient.fetchQuery({
        queryKey: ['gallery-group', gallery.group_key],
        queryFn: () => getGalleryGroup(gallery.group_key),
        staleTime: 30000,
      });
      setGroupImages(imgs);
      const idx = imgs.findIndex((i) => i.id === imageId);
      setLightboxIndex(idx >= 0 ? idx : 0);
    } catch (e) {
      console.error('ギャラリーグループの取得に失敗しました', e);
    } finally {
      setLoading(false);
    }
  };

  // リアクション更新をライトボックス表示中の画像へ反映
  const handleReaction = (
    imageId: number,
    reactions: { [key: number]: number },
    userReaction: number | null,
  ) => {
    setGroupImages((prev) =>
      prev.map((im) =>
        im.id === imageId ? { ...im, reaction_counts: reactions, user_reaction: userReaction } : im
      )
    );
  };

  // 1枚時は縦に伸ばさず「幅を絞る」ことで縦長を縦長として見せる。
  // 高さの上限はカード幅の84%（幅500pxで420px）で固定し、縦長画像はその高さに収まるまで
  // 幅を細める（= クロップ無しで全体が見える）。1:2 より縦長なものだけ中央クロップ。
  const SINGLE_MAX_H = 0.84;  // 高さ上限（カード幅に対する比）
  const SINGLE_AR_FLOOR = 0.5; // これより縦長は幅が痩せすぎるのでクロップに切り替え
  const single = (() => {
    const f = thumbs[0];
    const raw = f && f.width && f.height ? f.width / f.height : 16 / 9;
    const ar = Math.max(SINGLE_AR_FLOOR, Math.min(16 / 9, raw));
    // 高さ = 幅/ar ≤ SINGLE_MAX_H×カード幅 を満たす最大の幅比
    return { ar, widthPct: Math.min(1, SINGLE_MAX_H * ar) * 100 };
  })();

  // 複数枚は固定比率タイル＋cover なので、縦長画像を横長タイルに入れると帯状に切られて
  // 何の画像か分からなくなる。並びが縦長主体のときはグリッド全体を高くしてタイルを寄せる。
  // （縦長主体でも最大は正方形タイル止まり。TLの縦を占有しすぎないため）
  const portraits = thumbs.filter((im) => im.width && im.height && im.height > im.width).length;
  const isPortraitSet = portraits * 2 >= thumbs.length;
  // 2枚: 32:9=各タイル16:9 / 16:9=各タイル8:9
  // 3枚(2fr:1fr)・4枚(2×2): 16:9=各タイル16:9 / 4:3=3枚はほぼ正方形・4枚は各4:3
  // カード高さは幅500pxで最大375pxに収まる範囲に留める
  const gridAspect = thumbs.length === 2
    ? (isPortraitSet ? '16 / 9' : '32 / 9')
    : (isPortraitSet ? '4 / 3' : '16 / 9');

  // useOriginal: 1枚表示など大きく出す場合はサムネ(400px)では解像度が足りないので原寸を使う。
  // ネタバレでぼかす場合は元画像を送らずサムネのまま（転送量削減）。
  const tile = (img: typeof thumbs[number], i: number, useOriginal = false) => {
    const showOverlay = extra > 0 && i === thumbs.length - 1;
    const blurred = img.is_spoiler && !isOwner;
    const src = useOriginal && !blurred ? (img.image_url || img.thumb_url) : img.thumb_url;
    return (
      <Box
        onClick={() => openAt(img.id)}
        sx={{
          position: 'relative', width: '100%', height: '100%', overflow: 'hidden',
          cursor: 'pointer', backgroundColor: 'rgba(255,255,255,0.04)',
          '&:hover img': { transform: blurred ? undefined : 'scale(1.04)' },
        }}
      >
        {src && (
          <Box
            component="img"
            src={src}
            alt={blurred ? '' : (img.caption || '')}
            loading="lazy"
            sx={{
              width: '100%', height: '100%', objectFit: 'cover', display: 'block',
              transition: 'transform 0.35s cubic-bezier(0.4,0,0.2,1)',
              filter: blurred ? 'blur(24px)' : 'none',
            }}
          />
        )}
        {/* ネタバレ: 暗幕＋アイコン＋小さいラベル（タップでライトボックス表示） */}
        {blurred && (
          <Box sx={{
            position: 'absolute', inset: 0,
            backgroundColor: 'rgba(0,0,0,0.35)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.4,
            color: 'rgba(255,255,255,0.9)',
          }}>
            <VisibilityOffIcon sx={{ fontSize: 18 }} />
            <Typography sx={{ fontSize: '0.6rem', lineHeight: 1, letterSpacing: '0.05em' }}>ネタバレ</Typography>
          </Box>
        )}
        {showOverlay && (
          <Box sx={{
            position: 'absolute', inset: 0,
            backgroundColor: 'rgba(0,0,0,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', fontWeight: 600, fontSize: '1.25rem',
          }}>
            +{extra}
          </Box>
        )}
      </Box>
    );
  };

  return (
    <>
      <Box sx={{ position: 'relative', maxWidth: 500, borderRadius: '10px', overflow: 'hidden' }}>
        {thumbs.length === 1 && (
          <Box sx={{
            width: `${single.widthPct}%`, aspectRatio: `${single.ar}`,
            borderRadius: 'inherit', overflow: 'hidden',
          }}>
            {tile(thumbs[0], 0, true)}
          </Box>
        )}

        {thumbs.length === 2 && (
          <Box sx={{
            display: 'grid', gridTemplateColumns: '1fr 1fr', gridTemplateRows: 'minmax(0, 1fr)',
            gap: '3px', aspectRatio: gridAspect,
          }}>
            {thumbs.map((im, i) => (
              <Box key={im.id} sx={{ position: 'relative', minWidth: 0, minHeight: 0 }}>{tile(im, i)}</Box>
            ))}
          </Box>
        )}

        {thumbs.length === 3 && (
          <Box sx={{
            display: 'grid', gridTemplateColumns: '2fr 1fr',
            gridTemplateRows: 'minmax(0, 1fr) minmax(0, 1fr)',
            gap: '3px', aspectRatio: gridAspect,
          }}>
            <Box sx={{ gridRow: '1 / 3', position: 'relative', minWidth: 0, minHeight: 0 }}>{tile(thumbs[0], 0)}</Box>
            <Box sx={{ position: 'relative', minWidth: 0, minHeight: 0 }}>{tile(thumbs[1], 1)}</Box>
            <Box sx={{ position: 'relative', minWidth: 0, minHeight: 0 }}>{tile(thumbs[2], 2)}</Box>
          </Box>
        )}

        {thumbs.length >= 4 && (
          <Box sx={{
            display: 'grid', gridTemplateColumns: '1fr 1fr',
            gridTemplateRows: 'minmax(0, 1fr) minmax(0, 1fr)',
            gap: '3px', aspectRatio: gridAspect,
          }}>
            {thumbs.slice(0, 4).map((im, i) => (
              <Box key={im.id} sx={{ position: 'relative', minWidth: 0, minHeight: 0 }}>{tile(im, i)}</Box>
            ))}
          </Box>
        )}

        {loading && (
          <Box sx={{
            position: 'absolute', inset: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            backgroundColor: 'rgba(0,0,0,0.25)',
          }}>
            <CircularProgress size={22} sx={{ color: 'rgba(255,255,255,0.8)' }} />
          </Box>
        )}
      </Box>

      <GalleryLightbox
        images={groupImages}
        index={lightboxIndex}
        onClose={() => setLightboxIndex(null)}
        onNavigate={setLightboxIndex}
        isPublic={isPublic}
        onReactionUpdate={handleReaction}
      />
    </>
  );
};

export default GalleryActivityCard;
